import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Plus, Trash2, Webhook, AlertTriangle } from 'lucide-react';
import { webhooksApi } from '../api/services';
import type { WebhookSubscription } from '../types/models';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

const WEBHOOK_EVENTS = [
  'PlanProposed',
  'PlanApproved',
  'PlanClosed',
  'WorkItemCreated',
  'TranscriptAnalysed',
];

// ---------------------------------------------------------------------------
// WebhooksPage — admin-facing webhook subscription manager. List + create +
// toggle active + delete. Failure counter goes amber/rose once a webhook has
// recorded 5+ consecutive failures.
// ---------------------------------------------------------------------------

export default function WebhooksPage() {
  const [webhooks, setWebhooks] = useState<WebhookSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newWebhook, setNewWebhook] = useState<{ url: string; event: string; secret: string }>({
    url: '',
    event: WEBHOOK_EVENTS[0],
    secret: '',
  });
  const [toggling, setToggling] = useState<string | null>(null);

  const loadWebhooks = async () => {
    setLoading(true);
    setError(null);
    try {
      setWebhooks(await webhooksApi.getAll());
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load webhooks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadWebhooks(); }, []);

  const handleCreate = async () => {
    if (!newWebhook.url || !newWebhook.event) return;
    try {
      await webhooksApi.create({
        url: newWebhook.url,
        event: newWebhook.event,
        secret: newWebhook.secret || undefined,
      });
      setDialogOpen(false);
      setNewWebhook({ url: '', event: WEBHOOK_EVENTS[0], secret: '' });
      loadWebhooks();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to create webhook');
    }
  };

  const handleToggle = async (webhook: WebhookSubscription) => {
    setToggling(webhook.id);
    try {
      await webhooksApi.update(webhook.id, { isActive: !webhook.isActive });
      loadWebhooks();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to toggle webhook');
    } finally {
      setToggling(null);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this webhook subscription?')) return;
    try {
      await webhooksApi.delete(id);
      loadWebhooks();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to delete webhook');
    }
  };

  return (
    <div className="mx-auto max-w-[1200px] p-4 md:p-6">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Webhook className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Webhooks</h1>
            <p className="text-sm text-muted-foreground">
              Manage webhook subscriptions for external integrations
            </p>
          </div>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="gap-1.5">
          <Plus className="h-4 w-4" />
          Add Webhook
        </Button>
      </div>

      {loading && (
        <div className="mb-3 h-1 w-full overflow-hidden rounded-full bg-muted">
          <div className="h-full w-1/3 animate-pulse bg-primary" />
        </div>
      )}
      {error && (
        <Alert variant="error" className="mb-3">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {!loading && webhooks.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <Webhook className="mx-auto mb-3 h-12 w-12 text-muted-foreground/50" />
            <p className="mb-2 text-lg font-semibold">No webhook subscriptions</p>
            <p className="mb-3 text-sm text-muted-foreground">
              Create a webhook to receive event notifications from Cadence.
            </p>
            <Button variant="outline" onClick={() => setDialogOpen(true)} className="gap-1.5">
              <Plus className="h-4 w-4" />
              Add Webhook
            </Button>
          </CardContent>
        </Card>
      )}

      {webhooks.length > 0 && (
        <div className="overflow-hidden rounded-2xl border border-border bg-card/40 backdrop-blur">
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr className="border-b border-border text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                <th className="px-3 py-2">Event</th>
                <th className="px-3 py-2">URL</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Failures</th>
                <th className="px-3 py-2">Last Triggered</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {webhooks.map((wh) => (
                <tr key={wh.id} className="border-b border-border last:border-b-0 hover:bg-muted/30">
                  <td className="px-3 py-2">
                    <span className="inline-flex items-center rounded-full border border-primary/60 bg-transparent px-2 py-0.5 text-[11px] font-medium text-primary">
                      {wh.event}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <p className="max-w-[300px] truncate font-mono text-[12px]">{wh.url}</p>
                      </TooltipTrigger>
                      <TooltipContent>{wh.url}</TooltipContent>
                    </Tooltip>
                  </td>
                  <td className="px-3 py-2">
                    <label className="flex cursor-pointer items-center gap-2">
                      <Checkbox
                        checked={wh.isActive}
                        onCheckedChange={() => handleToggle(wh)}
                        disabled={toggling === wh.id}
                        aria-label={wh.isActive ? 'Deactivate webhook' : 'Activate webhook'}
                      />
                      <span
                        className={cn(
                          'inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium',
                          wh.isActive
                            ? 'border-emerald-500/60 text-emerald-700 dark:text-emerald-400'
                            : 'border-border text-muted-foreground',
                        )}
                      >
                        {wh.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </label>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1">
                      <p
                        className={cn(
                          'text-sm',
                          wh.failureCount >= 5 ? 'text-rose-600 dark:text-rose-400' : 'text-muted-foreground',
                        )}
                      >
                        {wh.failureCount}
                      </p>
                      {wh.failureCount >= 5 && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <AlertTriangle className="h-4 w-4 text-rose-500" />
                          </TooltipTrigger>
                          <TooltipContent>5+ consecutive failures — webhook may be unreachable</TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {wh.lastTriggeredAt
                      ? format(new Date(wh.lastTriggeredAt), 'dd MMM yyyy HH:mm')
                      : 'Never'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="icon-sm"
                          variant="ghost"
                          onClick={() => handleDelete(wh.id)}
                          className="text-rose-600 hover:text-rose-700 dark:text-rose-400"
                          aria-label="Delete webhook"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Delete webhook</TooltipContent>
                    </Tooltip>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Webhook Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => !o && setDialogOpen(false)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Webhook Subscription</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="wh-url">Webhook URL</Label>
              <Input
                id="wh-url"
                type="url"
                value={newWebhook.url}
                onChange={(e) => setNewWebhook(prev => ({ ...prev, url: e.target.value }))}
                placeholder="https://example.com/webhook"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="wh-event">Event</Label>
              <Select
                value={newWebhook.event}
                onValueChange={(v) => setNewWebhook(prev => ({ ...prev, event: v }))}
              >
                <SelectTrigger id="wh-event"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {WEBHOOK_EVENTS.map(ev => (
                    <SelectItem key={ev} value={ev}>{ev}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="wh-secret">Secret (optional)</Label>
              <Input
                id="wh-secret"
                type="password"
                value={newWebhook.secret}
                onChange={(e) => setNewWebhook(prev => ({ ...prev, secret: e.target.value }))}
                placeholder="Shared secret for signature verification"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={!newWebhook.url || !newWebhook.event}>
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
